import FbButton from 'components/FbUI/FbButton'
import confirm from 'components/dialogConfirm'
import { Editor } from 'components/Editor/Editor'

import { Sequence } from '../sequences_api'

interface SequenceDisplayProps {
  sequence: Sequence
  onEdit: () => void
  onDelete: () => void
  isDeleting: boolean
}

export function SequenceDisplay({
  sequence,
  onEdit,
  onDelete,
  isDeleting,
}: SequenceDisplayProps) {
  const handleDelete = async () => {
    const confirmed = await confirm(
      'Are you sure you want to delete this sequence? This action cannot be undone.',
      'Delete Sequence'
    )
    if (confirmed) {
      onDelete()
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Action buttons */}
      <div className="flex justify-end gap-2 pb-4">
        <FbButton variant="secondary" size="sm" onClick={onEdit}>
          Edit Sequence
        </FbButton>
        <FbButton
          variant="danger-outline"
          size="sm"
          onClick={handleDelete}
          loading={isDeleting}
        >
          Delete
        </FbButton>
      </div>

      {/* Steps */}
      <div className="flex-1 overflow-y-auto">
        <div className="relative">
          {/* Timeline connector line */}
          {sequence.steps.length > 1 && (
            <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-sage-lt z-0" />
          )}

          {sequence.steps
            .sort((a, b) => a.order - b.order)
            .map((step, index, sortedSteps) => (
              <div key={step.id}>
                {/* Step Card */}
                <div className="flex items-start gap-4 relative">
                  {/* Step Number Badge */}
                  <div className="flex-shrink-0 w-10 h-10 bg-surface-hover border border-fb-dark-green rounded-full flex items-center justify-center relative z-10">
                    <span className="text-sm font-semibold text-fb-dark-green">
                      {step.order}
                    </span>
                  </div>

                  {/* Content Card */}
                  <div className="flex-1 min-w-0 bg-white rounded-lg border border-line p-4 shadow-sm hover:shadow-md transition-shadow">
                    <h4 className="text-sm font-semibold text-fg mb-1">
                      {step.email_subject}
                    </h4>
                    <div className="text-sm text-fg-secondary line-clamp-3 [&_.ql-editor]:p-0 [&_.ql-editor]:min-h-0 [&_*]:m-0 [&_p]:leading-normal">
                      <Editor
                        value={step.email_body}
                        readOnly
                        disableToolbar
                        borderless
                      />
                    </div>
                  </div>
                </div>

                {/* Wait indicator between steps */}
                {index < sortedSteps.length - 1 && (
                  <div className="flex items-center gap-4 py-3">
                    {/* Spacer to align with cards */}
                    <div className="w-10 shrink-0" />
                    <div className="flex items-center gap-2 bg-white border border-sage-lt px-3 py-1.5 rounded-full shadow-sm">
                      <svg
                        className="w-3.5 h-3.5 text-fb-dark-green"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-xs font-medium text-fg-secondary">
                        {step.wait_in_days === 0
                          ? 'Send immediately'
                          : `Wait ${step.wait_in_days} day${step.wait_in_days !== 1 ? 's' : ''}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
